import { assetUrl } from "./assetUrl"
import { CardElement, ColorRank } from "../types/battle"
import { AFFILIATION_ELEMENTS } from "./affiliationInfo"
import { DustGrant, InstalledShard, ShardTier } from "../types/shards"
import { ELEMENT_NAMES } from "./guardians"

export const SHARD_SLOTS_BY_RANK: Record<ColorRank, number> = {
  WHITE: 2,
  GREEN: 2,
  BLUE: 3,
  PURPLE: 3,
  ORANGE: 4,
  RED: 4,
}

export const SHARD_TIER_LABELS: Record<ShardTier, string> = {
  COMMON: "Обычный",
  RARE: "Редкий",
  EPIC: "Эпический",
}

export const SHARD_TIER_ORDER: ShardTier[] = ["COMMON", "RARE", "EPIC"]

/** Fallback для Alert / plain text */
export const SHARD_ELEMENT_EMOJI: Record<CardElement, string> = {
  FIRE: "🔥",
  WATER: "💧",
  EARTH: "🪨",
  ICE: "❄️",
  WIND: "🌪",
  LIGHTNING: "⚡",
}

export const SHARD_ELEMENT_ICONS: Record<CardElement, string> = {
  FIRE: assetUrl("shards/fire_s.png"),
  WATER: assetUrl("shards/water_s.png"),
  EARTH: assetUrl("shards/earth_s.png"),
  ICE: assetUrl("shards/ice_s.png"),
  WIND: assetUrl("shards/wind_s.png"),
  LIGHTNING: assetUrl("shards/light_s.png"),
}

export const DUST_ELEMENT_ICONS: Record<CardElement, string> = {
  FIRE: assetUrl("shards/dust/fire_d.png"),
  WATER: assetUrl("shards/dust/water_d.png"),
  EARTH: assetUrl("shards/dust/earth_d.png"),
  ICE: assetUrl("shards/dust/ice_d.png"),
  WIND: assetUrl("shards/dust/wind_d.png"),
  LIGHTNING: assetUrl("shards/dust/light_d.png"),
}

export function resolveShardElementIcon(
  element: CardElement | null | undefined
): string | null {
  if (!element) return null
  return SHARD_ELEMENT_ICONS[element] ?? null
}

export function resolveDustElementIcon(
  element: CardElement | null | undefined
): string | null {
  if (!element) return null
  return DUST_ELEMENT_ICONS[element] ?? null
}

/** Иконка осколка на карте / в слотах — по стихии. */
export function resolveShardSlotIcon(
  shard: Pick<InstalledShard, "element" | "definition">
): string {
  return SHARD_ELEMENT_ICONS[shard.element]
}

export function totalShardDust(dust: Partial<Record<CardElement, number>>): number {
  return (Object.values(dust) as number[]).reduce(
    (sum, n) => sum + (typeof n === "number" ? n : 0),
    0
  )
}

/** Стихия с наибольшим запасом пыли — для иконки в шапке. */
export function dominantDustElement(
  dust: Partial<Record<CardElement, number>>
): CardElement {
  let best: CardElement = "FIRE"
  let bestAmount = -1
  for (const element of AFFILIATION_ELEMENTS) {
    const amount = dust[element] ?? 0
    if (amount > bestAmount) {
      bestAmount = amount
      best = element
    }
  }
  return best
}

/** Разница пыли до/после боя — для fallback, если бэк не прислал dustGranted. */
export function diffShardDust(
  before: Partial<Record<CardElement, number>>,
  after: Partial<Record<CardElement, number>>
): DustGrant | null {
  let bestElement: CardElement | null = null
  let bestDelta = 0

  for (const element of AFFILIATION_ELEMENTS) {
    const delta = (after[element] ?? 0) - (before[element] ?? 0)
    if (delta > bestDelta) {
      bestDelta = delta
      bestElement = element
    }
  }

  if (bestDelta <= 0) return null
  return { dustGranted: bestDelta, dustElement: bestElement }
}

export function formatDustGrantInline(grant: DustGrant): string {
  if (grant.dustElement) {
    return `+${grant.dustGranted} ${SHARD_ELEMENT_EMOJI[grant.dustElement]} ${ELEMENT_NAMES[grant.dustElement]}`
  }
  return `+${grant.dustGranted} пыли`
}
